import { FormField } from '@/global/components/Form'
import Grid from '@/global/components/ui/Grid'
import Dialog from '@/global/guides/Dialog'
import { useFormContext } from 'react-hook-form'
import { coordenada, type Endereco, local } from 'service/api'
import { z } from 'zod'

const schema = z.object({
    latitude: coordenada(90),
    longitude: coordenada(180),
    endereco: z.string().trim().min(1, 'Obrigatório'),
})

/** O reverse devolve as próprias coordenadas em texto quando não acha endereço. */
const SO_COORDENADAS = /^-?\d+\.\d+,\s?-?\d+\.\d+$/

export type EnderecoManual = z.infer<typeof schema>

type Props = {
    inicial: Partial<Record<keyof EnderecoManual, string>>
    onClose: () => void
    onConfirm: (valores: EnderecoManual) => void
}

/**
 * Pede coordenadas e endereço quando a busca falha ou o usuário prefere
 * digitar. Coordenada é o dado exato (centro da cerca); ao sair delas com o
 * endereço vazio, o endereço vem da API.
 */
export function DialogEnderecoManual({ inicial, onClose, onConfirm }: Props) {
    return (
        <Dialog.Root
            open
            onClose={onClose}
            onSubmit={onConfirm}
            schema={schema}
            defaultValues={inicial}
            width="lg"
        >
            <Dialog.Title>Informar endereço manualmente</Dialog.Title>
            <Dialog.Content>
                <p className="mb-4 text-sm text-muted-foreground">
                    Digite as coordenadas do local. Com o endereço vazio,
                    buscamos o endereço delas.
                </p>
                <Campos />
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                <Dialog.Actions.PrimaryButton closeOnSubmit>
                    Usar
                </Dialog.Actions.PrimaryButton>
            </Dialog.Actions>
        </Dialog.Root>
    )
}

function Campos() {
    const { getValues, setValue } = useFormContext<EnderecoManual>()

    const buscarEndereco = async () => {
        const { endereco, latitude, longitude } = getValues()
        if (endereco?.trim()) return

        const coordenadas = schema
            .pick({ latitude: true, longitude: true })
            .safeParse({ latitude, longitude })
        if (!coordenadas.success) return

        const achado = await local.endereco
            .get<Endereco>({
                latitude: Number(coordenadas.data.latitude),
                longitude: Number(coordenadas.data.longitude),
            })
            .catch(() => null)
        if (!achado || SO_COORDENADAS.test(achado.endereco)) return

        setValue('endereco', achado.endereco)
    }

    return (
        <Grid container>
            <Grid item xs={6}>
                <FormField
                    name="latitude"
                    label="Latitude"
                    onBlur={buscarEndereco}
                    required
                />
            </Grid>
            <Grid item xs={6}>
                <FormField
                    name="longitude"
                    label="Longitude"
                    onBlur={buscarEndereco}
                    required
                />
            </Grid>
            <Grid item xs={12}>
                <FormField name="endereco" label="Endereço" required />
            </Grid>
        </Grid>
    )
}
